import React, { useId, useMemo } from 'react';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  CampaignCard,
  MPFSectionHeading,
} from 'volto-ministerio-publico-federal/components/UI';

import { MAX_CAMPAIGNS } from './schema';
import type { CampaignsData } from './schema';
import { toCampaignCards, getPortalCampaignUids } from './helpers';
import { useCampaigns } from './useCampaigns';

interface ViewProps {
  data: CampaignsData;
  block: string;
  isEditMode?: boolean;
}

/** `not-prose` desliga a tipografia de texto corrido herdada do editor. */
const ROOT_CLASS = 'mpf-campaigns not-prose';

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-campaigns__placeholder">{children}</p>
);

const LoadingState: React.FC = () => (
  <div className="mpf-campaigns__loading" aria-busy="true" aria-live="polite">
    <span className="sr-only">Carregando campanhas…</span>
    <span
      aria-hidden="true"
      className="mpf-loading mpf-loading-spinner mpf-loading-lg text-primary dark:text-night-100"
    />
  </div>
);

export const MpfCampanhasBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
}) => {
  const configured = data?.items;
  const titleId = useId();

  const items = useMemo(
    () => (configured ?? []).slice(0, MAX_CAMPAIGNS),
    [configured],
  );

  const uids = useMemo(() => getPortalCampaignUids(items), [items]);

  const {
    items: brains,
    loading,
    loaded,
  } = useCampaigns({
    uids,
    blockId: block,
  });

  const isLoadingPortalPages = uids.length > 0 && (loading || !loaded);

  const cards = useMemo(
    () => (isLoadingPortalPages ? [] : toCampaignCards(items, brains)),
    [isLoadingPortalPages, items, brains],
  );

  const overflowCount = (configured?.length ?? 0) - MAX_CAMPAIGNS;
  const skippedCount = items.length - cards.length;

  const heading = data.title && (
    <MPFSectionHeading title={data.title} titleId={titleId} />
  );
  const sectionLabel = data.title ? { 'aria-labelledby': titleId } : {};

  if (items.length === 0) {
    return isEditMode ? (
      <div className={ROOT_CLASS}>
        <Notice>
          Escolha as campanhas na barra lateral. O destino pode ser uma página
          do portal ou o site próprio da campanha.
        </Notice>
      </div>
    ) : null;
  }

  if (isLoadingPortalPages) {
    return (
      <div className={ROOT_CLASS}>
        {heading}
        <LoadingState />
      </div>
    );
  }

  if (cards.length === 0) {
    return isEditMode ? (
      <div className={ROOT_CLASS}>
        {heading}
        <Notice>
          Nenhuma campanha pôde ser exibida. Confira se as páginas do portal
          continuam publicadas e se as campanhas externas têm título e link
          preenchido.
        </Notice>
      </div>
    ) : null;
  }

  return (
    <section className={ROOT_CLASS} {...sectionLabel}>
      {heading}

      <ul className="mpf-campaigns__body">
        {cards.map((campaign) => (
          <li key={campaign.key} className="mpf-campaigns__item">
            <CampaignCard campaign={campaign} />
          </li>
        ))}
      </ul>

      {isEditMode && overflowCount > 0 && (
        <Notice>
          {`O bloco exibe no máximo ${MAX_CAMPAIGNS} campanhas — as ${overflowCount} seguintes da lista não aparecem na página.`}
        </Notice>
      )}

      {isEditMode && skippedCount > 0 && (
        <Notice>
          {`${skippedCount} campanha(s) não puderam ser exibidas. Página do portal despublicada ou campanha externa sem título.`}
        </Notice>
      )}
    </section>
  );
};

export default withBlockExtensions(MpfCampanhasBlockView);
